import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import InteractiveCalendar from '../components/InteractiveCalendar';
import axios from 'axios';
import { AlertTriangle, MessageSquare, Wrench, CheckCircle, Clock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPrestataire = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const [incidents, setIncidents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [incRes, convRes] = await Promise.all([
        axios.get(`${API}/incidents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setIncidents(incRes.data);
      setConversations(convRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  // Polling pour mise à jour en temps réel toutes les 5 secondes
  useEffect(() => {
    if (!token) return;
    
    const pollInterval = setInterval(() => {
      fetchDataQuietly();
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [token]);

  const fetchDataQuietly = async () => {
    try {
      const [incRes, convRes] = await Promise.all([
        axios.get(`${API}/incidents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setIncidents(incRes.data);
      setConversations(convRes.data.slice(0, 3));
    } catch (error) {
      // Silencieux
    }
  };

  const getStatusInfo = (status) => {
    const statuses = {
      'Non traité': { color: 'bg-red-500', icon: AlertTriangle },
      'En cours': { color: 'bg-yellow-500', icon: Clock },
      'Assigné': { color: 'bg-blue-500', icon: User },
      'Pris en charge': { color: 'bg-purple-500', icon: Wrench },
      'Résolu': { color: 'bg-green-500', icon: CheckCircle },
      'Résolu par gestionnaire': { color: 'bg-green-500', icon: CheckCircle },
      'Résolu par prestataire': { color: 'bg-emerald-500', icon: CheckCircle },
    };
    return statuses[status] || { color: 'bg-gray-500', icon: AlertTriangle };
  };

  // Statistiques des incidents
  const incidentsEnAttente = incidents.filter(i => i.status === 'Assigné').length;
  const incidentsPrisEnCharge = incidents.filter(i => i.status === 'Pris en charge').length;
  const incidentsResolus = incidents.filter(i => 
    i.status === 'Résolu' || i.status === 'Résolu par prestataire' || i.status === 'Résolu par gestionnaire'
  ).length;

  if (loading) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title={`Bonjour ${user?.prenom || ''} 👋`}
            onMenuClick={onMenuClick}
          />
        )}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <MobileHeader 
          title={`Bonjour ${user?.prenom || ''} 👋`}
          onMenuClick={onMenuClick}
        />
      )}

      <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`}>
        {/* Desktop Title */}
        {!isMobile && (
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Bonjour {user?.prenom} 👋</h1>
            <span className="text-sm text-gray-500">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
        )}

        {/* Statistiques rapides */}
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          <div className="bg-white rounded-xl shadow-sm p-3 md:p-4 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">En attente</p>
                <p className="text-xl md:text-2xl font-bold text-blue-600">{incidentsEnAttente}</p>
              </div>
              <User className="text-blue-500 hidden md:block" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 md:p-4 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Pris en charge</p>
                <p className="text-xl md:text-2xl font-bold text-purple-600">{incidentsPrisEnCharge}</p>
              </div>
              <Wrench className="text-purple-500 hidden md:block" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 md:p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Résolus</p>
                <p className="text-xl md:text-2xl font-bold text-green-600">{incidentsResolus}</p>
              </div>
              <CheckCircle className="text-green-500 hidden md:block" size={32} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Colonne gauche: Messages + Accès rapide */}
          <div className="space-y-4 md:space-y-6">
            {/* Messages récents */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold text-gray-800">Messages récents</h2>
                <button 
                  onClick={() => navigate('/messagerie')}
                  className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Voir tout →
                </button>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                {conversations.length === 0 ? (
                  <div className="text-center py-4">
                    <MessageSquare className="mx-auto text-gray-300 mb-2" size={isMobile ? 28 : 32} />
                    <p className="text-gray-500 text-sm">Aucun message</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div 
                      key={conv.id} 
                      onClick={() => navigate('/messagerie')}
                      className="p-2.5 md:p-3 border border-gray-100 rounded-xl hover:border-[#00D3A8] cursor-pointer transition-colors"
                    >
                      <div className="flex items-start space-x-2.5 md:space-x-3">
                        <div className="w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center text-white text-xs md:text-sm font-medium flex-shrink-0">
                          {conv.participants?.[0]?.prenom?.[0] || 'M'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 text-sm truncate">
                            {conv.participants?.map(p => p.prenom).join(', ') || 'Conversation'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {conv.last_message?.content || 'Nouvelle conversation'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Accès rapide - Déplacé en bas à gauche */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Accès rapide</h2>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  onClick={() => navigate('/incidents')}
                  className="p-3 md:p-4 border border-gray-100 rounded-xl hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center"
                  data-testid="quick-access-incidents"
                >
                  <div className="text-xl md:text-2xl mb-1">🚨</div>
                  <div className="text-xs md:text-sm font-medium text-gray-700">Incidents</div>
                </button>
                <button
                  onClick={() => navigate('/messagerie')}
                  className="p-3 md:p-4 border border-gray-100 rounded-xl hover:border-[#00D3A8] hover:bg-gray-50 transition-all text-center"
                  data-testid="quick-access-messagerie"
                >
                  <div className="text-xl md:text-2xl mb-1">💬</div>
                  <div className="text-xs md:text-sm font-medium text-gray-700">Messagerie</div>
                </button>
              </div>
            </div>
          </div>

          {/* Colonne principale: Mes incidents */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* Mes incidents */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-base md:text-lg font-semibold text-gray-800">Mes incidents</h2>
                <button 
                  onClick={() => navigate('/incidents')}
                  className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Voir tout →
                </button>
              </div>
              
              <div className="space-y-2 md:space-y-3">
                {incidents.length === 0 ? (
                  <div className="text-center py-6 md:py-8">
                    <AlertTriangle className="mx-auto text-gray-300 mb-3" size={isMobile ? 32 : 40} />
                    <p className="text-gray-500 text-sm">Aucun incident assigné</p>
                  </div>
                ) : (
                  incidents.slice(0, 5).map((incident) => {
                    const statusInfo = getStatusInfo(incident.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <div
                        key={incident.id}
                        onClick={() => navigate('/incidents')}
                        className="p-3 md:p-4 border border-gray-100 rounded-xl hover:shadow-md hover:border-[#00D3A8] transition-all cursor-pointer"
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`p-1.5 md:p-2 rounded-full ${statusInfo.color} bg-opacity-10 flex-shrink-0`}>
                            <StatusIcon className={statusInfo.color.replace('bg-', 'text-')} size={isMobile ? 16 : 20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="font-medium text-gray-800 text-sm md:text-base truncate">{incident.titre}</h3>
                              <span className={`text-[10px] md:text-xs px-2 py-0.5 md:py-1 rounded-full text-white flex-shrink-0 ${statusInfo.color}`}>
                                {incident.status}
                              </span>
                            </div>
                            {/* Adresse complète de l'immeuble - bien visible */}
                            {incident.building && (
                              <div className="flex items-start space-x-1 mt-1 text-xs md:text-sm text-[#00D3A8] font-medium">
                                <span className="flex-shrink-0">📍</span>
                                <span className="line-clamp-2">
                                  <span className="font-semibold">{incident.building.nom}</span>
                                  {incident.building.adresse && (
                                    <span className="text-gray-600 font-normal"> - {incident.building.adresse}</span>
                                  )}
                                  {(incident.building.code_postal || incident.building.ville) && (
                                    <span className="text-gray-600 font-normal">
                                      {incident.building.code_postal && ` ${incident.building.code_postal}`}
                                      {incident.building.ville && ` ${incident.building.ville}`}
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                            <p className="text-xs md:text-sm text-gray-500 mt-1 truncate">{incident.description}</p>
                            <div className="flex items-center space-x-2 md:space-x-3 mt-1.5 md:mt-2 text-[10px] md:text-xs text-gray-400">
                              <span>📅 {new Date(incident.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            {/* Calendrier interactif pour voir les interventions planifiées */}
            <div className="hidden sm:block">
              <InteractiveCalendar />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardPrestataire;
